import React, { useState, useEffect } from 'react';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { ChevronDown, ChevronRight, FileText, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

// ══════════════════════════════════════════════════════════════════════════════
// BIOLOGICAL PROGRESS NARRATIVE — REPORTS
// ══════════════════════════════════════════════════════════════════════════════

// Map numeric stability to readable pattern
function overallPattern(nss) {
  if (nss == null) return null;
  if (nss >= 70) return { label: 'stable', description: 'Your system appears to be maintaining a steady state with functional consistency across the day.' };
  if (nss >= 60) return { label: 'improving', description: 'Your system shows signs of gradual stabilization, with more predictable responses emerging.' };
  if (nss >= 45) return { label: 'fluctuating', description: 'Your system shows variability between periods of stability and instability. This is a common pattern during active adjustment.' };
  return { label: 'variable', description: 'Your system is experiencing notable shifts. These patterns often reflect the body adjusting to changes in routine or support.' };
}

// Convert trigger data to readable cause → effect insights
function buildInsights(triggers, stateAnalysis) {
  const insights = [];

  // From trigger correlations
  if (triggers?.length > 0) {
    triggers.slice(0, 3).forEach(t => {
      if (Math.abs(t.impact) > 15) {
        const direction = t.impact < 0 ? 'appears to reduce stability' : 'appears to support stability';
        insights.push({
          cause: t.trigger,
          effect: direction,
          text: `When ${t.trigger.toLowerCase()} is present, it ${direction}.`
        });
      }
    });
  }

  // From state analysis
  if (stateAnalysis?.best_state && stateAnalysis?.worst_state) {
    const stateNames = { off: 'medication gap periods', on: 'active medication periods', peak: 'peak response periods' };
    insights.push({
      cause: 'Timing consistency',
      effect: 'state variation',
      text: `Your system responds most smoothly during ${stateNames[stateAnalysis.best_state] || stateAnalysis.best_state}, suggesting timing plays a meaningful role.`
    });
  }

  return insights.slice(0, 4);
}


export default function SubscriberReports() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  // Data sources
  const [neuroSummary, setNeuroSummary] = useState(null);
  const [weeklyDigest, setWeeklyDigest] = useState(null);
  const [twentyDayReports, setTwentyDayReports] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [labSummary, setLabSummary] = useState(null);

  const [showDocuments, setShowDocuments] = useState(false);
  const [expandedReport, setExpandedReport] = useState(null);

  const subscriberId = user?.role === 'caregiver' ? user?.linked_subscriber_id : user?.id;

  useEffect(() => {
    if (!subscriberId) { setLoading(false); return; }

    const weekStart = (() => {
      const d = new Date();
      const day = d.getDay();
      const mon = new Date(d);
      mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      return mon.toISOString().split('T')[0];
    })();

    Promise.allSettled([
      axios.get(`${API}/mindflo/${subscriberId}/summary?days=20`),
      axios.get(`${API}/mindflo/${subscriberId}/weekly-digest?week_start=${weekStart}`),
      axios.get(`${API}/reports/twenty-day/${subscriberId}`),
      axios.get(`${API}/documents?subscriber_id=${subscriberId}`),
      axios.get(`${API}/admin/lab-results/${subscriberId}/summary`)
    ]).then(([neuroRes, digestRes, reportsRes, docsRes, labRes]) => {
      if (neuroRes.status === 'fulfilled') setNeuroSummary(neuroRes.value.data);
      if (digestRes.status === 'fulfilled') setWeeklyDigest(digestRes.value.data);
      if (reportsRes.status === 'fulfilled') {
        const data = reportsRes.value.data;
        setTwentyDayReports(Array.isArray(data) ? data : []);
      }
      if (docsRes.status === 'fulfilled') {
        setDocuments(Array.isArray(docsRes.value.data) ? docsRes.value.data : []);
      }
      if (labRes.status === 'fulfilled') setLabSummary(labRes.value.data);
    }).finally(() => setLoading(false));
  }, [subscriberId]);

  // Document download handler
  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { /* silently fail */ }
  };

  if (loading) {
    return (
      <BiologicalLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-bio-accent/30 border-t-bio-accent rounded-full animate-spin" />
        </div>
      </BiologicalLayout>
    );
  }

  const hasNeuro = neuroSummary?.has_data;
  const hasDigest = weeklyDigest?.has_data;
  const hasReports = twentyDayReports.length > 0;
  const hasLab = labSummary?.has_data;
  const hasAnyData = hasNeuro || hasDigest || hasReports || hasLab;

  // Derived narrative content
  const avgNss = neuroSummary?.averages?.nss;
  const pattern = overallPattern(avgNss);
  const triggers = weeklyDigest?.trigger_correlation || [];
  const stateAnalysis = neuroSummary?.state_analysis;
  const insights = buildInsights(triggers, stateAnalysis);
  const interpretations = neuroSummary?.interpretations || [];
  const positiveSignals = weeklyDigest?.positive_signals || [];

  // Opening sentence — the anchor
  const dayRange = hasNeuro ? (neuroSummary?.entry_count || 0) : 0;
  const openingSentence = hasNeuro
    ? `Over the past ${dayRange > 5 ? '20' : 'few'} days, your system ${pattern?.label === 'stable' ? 'shows maintaining stability with functional consistency' : pattern?.label === 'improving' ? 'shows signs of gradual stabilization' : 'has been navigating variable patterns — a natural part of the adjustment process'}.`
    : 'Your biological progress narrative will emerge as observations accumulate.';

  // Responsive zone — where system improves
  const bestState = stateAnalysis?.best_state;
  const responsiveZone = bestState
    ? `Your system appears most responsive during ${bestState === 'on' ? 'active medication periods' : bestState === 'peak' ? 'peak response windows' : 'transition periods'}. This suggests alignment between timing and biological rhythm.`
    : null;

  // Risk zone — where instability appears
  const worstState = stateAnalysis?.worst_state;
  const riskZone = worstState
    ? `Instability is most visible during ${worstState === 'off' ? 'medication gap periods' : worstState === 'peak' ? 'peak response windows where overshoot may occur' : 'transitions between states'}. Maintaining consistency in these windows may help.`
    : null;

  // One focus recommendation
  const topTrigger = triggers.length > 0 ? triggers[0] : null;
  const focusRecommendation = topTrigger
    ? `Focus on managing ${topTrigger.trigger.toLowerCase()} — it appears to have the strongest influence on your stability. Even small improvements in this area may have a noticeable effect.`
    : (hasNeuro ? 'Continue maintaining your current routine. Consistency appears to be the strongest contributor to stability.' : null);

  // Execution reality
  const execReality = hasDigest
    ? weeklyDigest?.snapshot?.interpretation || null
    : null;

  // Key drivers from triggers
  const keyDrivers = triggers.slice(0, 3).map(t => ({
    name: t.trigger,
    explanation: `Associated with a ${Math.abs(t.impact).toFixed(0)}-point ${t.impact < 0 ? 'reduction' : 'improvement'} in stability when present.`
  }));

  return (
    <BiologicalLayout>
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="reports-progress-narrative">

        {/* ─── Title ─────────────────────────────────────── */}
        <div className="mb-14">
          <p className="text-[11px] uppercase tracking-[0.3em] text-bio-faint mb-3 font-['Work_Sans']"
             data-testid="reports-label">
            Your Biological Progress
          </p>
          <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-light text-bio-primary leading-tight"
              data-testid="reports-title">
            Progress Narrative
          </h1>
        </div>


        {/* ─── 1. Opening Sentence (Anchor) ──────────────── */}
        <section className="mb-16" data-testid="reports-opening">
          <p className="text-base sm:text-lg text-bio-body leading-[1.8] font-['Work_Sans'] font-light">
            {openingSentence}
          </p>
        </section>


        {/* ─── 2. Overall Pattern ─────────────────────────── */}
        {pattern && (
          <section className="mb-16" data-testid="reports-overall-pattern">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              Overall pattern
            </p>
            <p className="text-bio-strong text-sm sm:text-base leading-relaxed font-['Work_Sans']">
              {pattern.description}
            </p>
          </section>
        )}


        {/* ─── 3. Biological Pattern Section ──────────────── */}
        {interpretations.length > 0 && (
          <section className="mb-16" data-testid="reports-biological-pattern">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              What is happening internally
            </p>
            <div className="space-y-3">
              {interpretations.map((text, i) => (
                <p key={i} className="text-bio-secondary text-sm leading-[1.85] font-['Work_Sans']"
                   data-testid={`reports-interpretation-${i}`}>
                  {text}
                </p>
              ))}
            </div>
          </section>
        )}


        {/* ─── 4. Key Drivers ────────────────────────────── */}
        {keyDrivers.length > 0 && (
          <section className="mb-16" data-testid="reports-key-drivers">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
              What appears to shape your stability
            </p>
            <div className="space-y-4">
              {keyDrivers.map((d, i) => (
                <div key={i} className="flex items-start gap-4" data-testid={`reports-driver-${i}`}>
                  <span className="text-[11px] text-bio-ghost font-['Work_Sans'] mt-0.5 w-4 flex-shrink-0">
                    {i + 1}.
                  </span>
                  <div>
                    <p className="text-bio-strong text-sm font-['Work_Sans']">{d.name}</p>
                    <p className="text-bio-subtle text-[11px] font-['Work_Sans'] mt-0.5">{d.explanation}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}


        {/* ─── 5. Execution Reality ──────────────────────── */}
        {execReality && (
          <section className="mb-16" data-testid="reports-execution-reality">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              How your days have looked
            </p>
            <p className="text-bio-secondary text-sm leading-[1.85] font-['Work_Sans']">
              {execReality}
            </p>
          </section>
        )}


        {/* ─── 6. Neuro-State Response ───────────────────── */}
        {hasNeuro && (
          <section className="mb-16" data-testid="reports-neuro-response">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              Your inner response
            </p>
            <div
              className="rounded-2xl p-6"
              style={{ backgroundColor: 'rgba(126, 107, 156, 0.18)', border: '1px solid rgba(126, 107, 156, 0.18)' }}
            >
              <p className="text-bio-secondary text-sm leading-[1.85] font-['Work_Sans']">
                Across your recent observations, {avgNss >= 60
                  ? 'your neurological stability has remained within a functional range, suggesting your support structure is providing meaningful benefit.'
                  : avgNss >= 45
                    ? 'your system shows moderate variability between states. Emotional stability appears slightly more preserved than cognitive consistency.'
                    : 'your system has been experiencing notable shifts. This variability often reflects periods of active change or adjustment.'}
              </p>
              {positiveSignals.length > 0 && positiveSignals[0] !== 'Continue monitoring for emerging positive patterns' && (
                <p className="text-bio-subtle text-[11px] font-['Work_Sans'] mt-3">
                  {positiveSignals[0]}
                </p>
              )}
            </div>
          </section>
        )}


        {/* ─── 7. Cause → Effect Insights ────────────────── */}
        {insights.length > 0 && (
          <section className="mb-16" data-testid="reports-cause-effect">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
              Observed connections
            </p>
            <div className="space-y-3">
              {insights.map((ins, i) => (
                <p key={i} className="text-bio-secondary text-sm leading-relaxed font-['Work_Sans'] pl-4"
                   style={{ borderLeft: '2px solid rgba(200, 155, 123, 0.22)' }}
                   data-testid={`reports-insight-${i}`}>
                  {ins.text}
                </p>
              ))}
            </div>
          </section>
        )}


        {/* ─── 8. Responsive Zone ────────────────────────── */}
        {responsiveZone && (
          <section className="mb-16" data-testid="reports-responsive-zone">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              Where your system responds well
            </p>
            <p className="text-bio-secondary text-sm leading-[1.85] font-['Work_Sans']">
              {responsiveZone}
            </p>
          </section>
        )}


        {/* ─── 9. Risk Zone ──────────────────────────────── */}
        {riskZone && (
          <section className="mb-16" data-testid="reports-risk-zone">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              Where to be mindful
            </p>
            <p className="text-bio-muted text-sm leading-[1.85] font-['Work_Sans']">
              {riskZone}
            </p>
          </section>
        )}


        {/* ─── 10. One Focus Recommendation ──────────────── */}
        {focusRecommendation && (
          <section className="mb-16" data-testid="reports-focus-recommendation">
            <div
              className="rounded-2xl p-6 sm:p-8"
              style={{ backgroundColor: 'rgba(200, 155, 123, 0.18)', border: '1px solid rgba(200, 155, 123, 0.20)' }}
            >
              <p className="text-[10px] uppercase tracking-[0.25em] text-bio-accent-soft mb-3 font-['Work_Sans']">
                One thing to focus on
              </p>
              <p className="text-bio-strong text-sm sm:text-base leading-[1.85] font-['Work_Sans']">
                {focusRecommendation}
              </p>
            </div>
          </section>
        )}


        {/* ─── Lab Validation Context ────────────────────── */}
        {hasLab && (
          <section className="mb-16" data-testid="reports-lab-context">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              Biological markers context
            </p>
            <p className="text-bio-subtle text-sm leading-relaxed font-['Work_Sans']">
              Your latest biological markers have been reviewed across {labSummary.total_markers_tested} areas.
              {labSummary.action_required_markers?.length > 0
                ? ` ${labSummary.action_required_markers.length} marker${labSummary.action_required_markers.length > 1 ? 's' : ''} suggest${labSummary.action_required_markers.length === 1 ? 's' : ''} areas where targeted support may be beneficial.`
                : ' Current levels are being monitored as part of your ongoing support.'}
            </p>
          </section>
        )}


        {/* ─── 11. Closing Line ──────────────────────────── */}
        <section className="mb-16" data-testid="reports-closing">
          <p className="text-[11px] text-bio-faint leading-relaxed font-['Work_Sans'] text-center max-w-lg mx-auto">
            Stability appears to improve when consistency is maintained. Continue supporting this pattern.
          </p>
        </section>


        {/* ─── 20-Day Reports (if any) ───────────────────── */}
        {hasReports && (
          <section className="mb-14" data-testid="reports-twenty-day">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
              Previous progress reports
            </p>
            <div className="space-y-3">
              {twentyDayReports.map((r, i) => (
                <div key={r.id || i}>
                  <button
                    onClick={() => setExpandedReport(expandedReport === i ? null : i)}
                    className="w-full flex items-center gap-3 text-left hover:bg-bio-surface-raised/50 rounded-lg px-3 py-2.5 transition-colors"
                    data-testid={`twenty-day-report-${i}`}
                  >
                    {expandedReport === i
                      ? <ChevronDown className="w-3.5 h-3.5 text-bio-ghost" strokeWidth={1} />
                      : <ChevronRight className="w-3.5 h-3.5 text-bio-ghost" strokeWidth={1} />
                    }
                    <span className="text-sm text-bio-secondary font-['Work_Sans']">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Report'}
                    </span>
                  </button>
                  {expandedReport === i && (
                    <div className="pl-7 pr-3 pb-4 space-y-3">
                      {r.summary && <p className="text-bio-muted text-sm leading-relaxed font-['Work_Sans']">{r.summary}</p>}
                      {r.improvements?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-bio-faint uppercase mb-1 font-['Work_Sans']">Improvements noted</p>
                          {r.improvements.map((imp, j) => (
                            <p key={j} className="text-bio-subtle text-[11px] font-['Work_Sans']">{imp}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}


        {/* ─── Documents (Optional, Collapsed) ───────────── */}
        {documents.length > 0 && (
          <section className="mb-10" data-testid="reports-documents">
            <button
              onClick={() => setShowDocuments(!showDocuments)}
              className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-bio-ghost hover:text-bio-faint transition-colors font-['Work_Sans']"
              data-testid="reports-documents-toggle"
            >
              {showDocuments
                ? <ChevronDown className="w-3 h-3" strokeWidth={1} />
                : <ChevronRight className="w-3 h-3" strokeWidth={1} />
              }
              Your documents ({documents.length})
            </button>
            {showDocuments && (
              <div className="mt-4 space-y-2 pl-5" style={{ borderLeft: '1px solid rgba(0,0,0,0.06)' }}>
                {documents.map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 py-2">
                    <FileText className="w-3.5 h-3.5 text-bio-ghost" strokeWidth={1} />
                    <span className="text-sm text-bio-muted font-['Work_Sans'] flex-1">{doc.title}</span>
                    <span className="text-[10px] text-bio-ghost font-['Work_Sans']">
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ''}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(doc)}
                      className="h-6 w-6 p-0 text-bio-ghost hover:text-bio-subtle"
                    >
                      <Download className="w-3 h-3" strokeWidth={1} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}


        {/* ─── Empty State ───────────────────────────────── */}
        {!hasAnyData && (
          <section className="text-center py-10">
            <p className="text-bio-muted text-sm font-['Work_Sans'] leading-relaxed max-w-md mx-auto">
              Your biological progress narrative will emerge as observations accumulate.
              Regular assessments and daily tracking create the foundation for meaningful insights.
            </p>
          </section>
        )}


        {/* ─── Closing breath ──────────────────────────── */}
        <div className="pt-4 pb-4">
          <div className="w-8 h-px bg-bio-border mx-auto" />
        </div>

      </div>
    </BiologicalLayout>
  );
}
